import { formatGhs } from 'pages/FuelManagement/Shared/fuelFormatters'
import { formatDateForDisplay } from 'utils/helpers'
import { ISelectValue } from 'constants/globalTypes'
import {
  InventoryAdjustmentReason,
  InventoryChargeToKind,
  InventoryCountCadence,
  InventoryItemNature,
  InventoryStockTakeStatus,
} from 'types/inventoryEnums.interface'
import {
  ADJUSTMENT_REASON_LABEL,
  CHARGE_TO_KIND_LABEL,
  COUNT_CADENCE_LABEL,
  ITEM_NATURE_TO_UI,
  STOCK_TAKE_APPROVAL_ROLE_LABEL,
} from './inventoryEnumMaps'

export const signedGhs = (value: number): string =>
  value < 0 ? `−${formatGhs(Math.abs(value))}` : value > 0 ? `+${formatGhs(value)}` : formatGhs(0)

export const STOCK_TAKE_DANGER_PCT = 1

export const stockTakeVarianceTone = (
  varianceGhs: number | null,
  sheetValueGhs: number | null,
): 'danger' | 'normal' => {
  if (varianceGhs == null || varianceGhs >= 0 || !sheetValueGhs) return 'normal'
  return (Math.abs(varianceGhs) / sheetValueGhs) * 100 > STOCK_TAKE_DANGER_PCT ? 'danger' : 'normal'
}

export const countProgressLabel = (counted: number, total: number | null): string =>
  total == null ? '—' : `${counted} / ${total}`

export const deviationBannerText = (breachedLines: number, limitGhs: number): string =>
  `${breachedLines} ${breachedLines === 1 ? 'line exceeds' : 'lines exceed'} the ${formatGhs(limitGhs)} deviation limit — supervisor approval required`

export const STOCK_TAKE_APPROVAL_BANNER = 'Adjustments posted to stock on approval'
export const TRANSFER_RECEIPT_OK_NOTE = 'Counted OK — no variance'

export const STOCK_TAKE_CADENCE_OPTIONS: ISelectValue[] = [
  { value: InventoryCountCadence.Weekly, label: COUNT_CADENCE_LABEL[InventoryCountCadence.Weekly] },
  { value: InventoryCountCadence.Monthly, label: COUNT_CADENCE_LABEL[InventoryCountCadence.Monthly] },
  {
    value: InventoryCountCadence.Quarterly,
    label: COUNT_CADENCE_LABEL[InventoryCountCadence.Quarterly],
  },
]

export const ADJUSTMENT_REASON_OPTIONS: ISelectValue[] = [
  {
    value: InventoryAdjustmentReason.Damage,
    label: ADJUSTMENT_REASON_LABEL[InventoryAdjustmentReason.Damage],
  },
  {
    value: InventoryAdjustmentReason.Theft,
    label: ADJUSTMENT_REASON_LABEL[InventoryAdjustmentReason.Theft],
  },
  {
    value: InventoryAdjustmentReason.CountVariance,
    label: ADJUSTMENT_REASON_LABEL[InventoryAdjustmentReason.CountVariance],
  },
  {
    value: InventoryAdjustmentReason.Waste,
    label: ADJUSTMENT_REASON_LABEL[InventoryAdjustmentReason.Waste],
  },
  {
    value: InventoryAdjustmentReason.Expiry,
    label: ADJUSTMENT_REASON_LABEL[InventoryAdjustmentReason.Expiry],
  },
]

export const CHARGE_KIND_OPTIONS: ISelectValue[] = [
  {
    value: InventoryChargeToKind.CostCenter,
    label: CHARGE_TO_KIND_LABEL[InventoryChargeToKind.CostCenter],
  },
  { value: InventoryChargeToKind.Machine, label: CHARGE_TO_KIND_LABEL[InventoryChargeToKind.Machine] },
  {
    value: InventoryChargeToKind.WorkOrder,
    label: CHARGE_TO_KIND_LABEL[InventoryChargeToKind.WorkOrder],
  },
]

export const ITEM_NATURE_OPTIONS: ISelectValue[] = [
  { value: InventoryItemNature.Consumable, label: ITEM_NATURE_TO_UI[InventoryItemNature.Consumable] },
  { value: InventoryItemNature.Returnable, label: ITEM_NATURE_TO_UI[InventoryItemNature.Returnable] },
]

export const chargeToDisplay = (
  chargeTo: { kind: number | string; label: string } | null | undefined,
): string => {
  if (!chargeTo) return '—'
  const kindLabel =
    typeof chargeTo.kind === 'number'
      ? CHARGE_TO_KIND_LABEL[chargeTo.kind] ?? String(chargeTo.kind)
      : String(chargeTo.kind).replace(/_/g, ' ')
  return `${kindLabel} · ${chargeTo.label}`
}

export const counterLinesSummary = (lines: { quantity: number; name: string }[]): string => {
  if (!lines.length) return '—'
  if (lines.length === 1) return `${lines[0].name} ×${lines[0].quantity}`
  const units = lines.reduce((sum, line) => sum + line.quantity, 0)
  return `${lines.length} lines · ${units} units`
}

export const issueLinesSummary = (
  lines: { quantity: number; inventoryItem?: { name?: string } | null }[] | null | undefined,
): string => {
  if (!lines?.length) return '—'
  if (lines.length === 1) {
    const line = lines[0]
    return `${line.inventoryItem?.name ?? 'Item'} ×${line.quantity}`
  }
  const units = lines.reduce((sum, line) => sum + (line.quantity || 0), 0)
  return `${lines.length} lines · ${units} units`
}

export const stockTakeApproverLabel = (role: number | null): string =>
  role != null && STOCK_TAKE_APPROVAL_ROLE_LABEL[role]
    ? STOCK_TAKE_APPROVAL_ROLE_LABEL[role]
    : STOCK_TAKE_APPROVAL_ROLE_LABEL[1]

export const formatInvGhs = (value: number): string => formatGhs(value)

export const unitsHintLabel = (
  purchaseUnit: string | null | undefined,
  issueUnit: string | null | undefined,
): string | null => {
  if (!purchaseUnit && !issueUnit) return null
  const buy = purchaseUnit ? `buy: ${purchaseUnit}` : null
  const issue = issueUnit ? `issue: ${issueUnit}` : null
  return [buy, issue].filter(Boolean).join(' · ')
}

export const invDateTimeLabel = (value: string | Date | null | undefined): string => {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '—'
  const now = new Date()
  const time = date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
  const isToday =
    date.getDate() === now.getDate() &&
    date.getMonth() === now.getMonth() &&
    date.getFullYear() === now.getFullYear()
  if (isToday) return `Today ${time}`
  return `${formatDateForDisplay(date)} ${time}`
}

export const isStockTakeEditable = (status: InventoryStockTakeStatus): boolean =>
  status === InventoryStockTakeStatus.InProgress

export const isStockTakeBlindActive = (blind: boolean, status: InventoryStockTakeStatus): boolean =>
  blind && status === InventoryStockTakeStatus.InProgress